import { Box,Typography, Button, Link, CircularProgress } from '@mui/material';
import React, { useContext, useState } from "react";
import { AuthContext } from "../../AuthContext";
import { Link as RouterLink } from 'react-router-dom';
import type { SxProps, Theme } from "@mui/material/styles";
import { BASE_URL, imageUrl } from '../../api';
import { apiFetch, HttpError } from "@shared/api";

type ResendConfirmEmailResponse = {
    message?: string;
};

type TavelButtonProps = {
    text?: React.ReactNode;
    children?: React.ReactNode;
    iconSrc?: string;
    variant?: "contained" | "outlined" | "text";
    appearance?: "filled" | "outline";
    href?: string;
    to?: string;
    component?: React.ElementType;
    bgColor?: string;
    hoverColor?: string;
    activeColor?: string;
    textColor?: string;
    size?: "mini" | "small" | "mediumsmall" | "medium" | "";
    sx?: SxProps<Theme>;
} & Omit<React.ComponentProps<typeof Button>, "color" | "variant" | "sx">;

// Узкая кнопка для карточки путешевствия
function TavelButton({
    text,
    children,
    iconSrc,
    variant = "contained",
    appearance = "filled",
    href,
    to,
    component,
    bgColor = "var(--grey-color)",
    hoverColor,
    activeColor,
    textColor = "inherit",
    size = "medium",
    sx: sxProp,
    ...props
    }: TavelButtonProps) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;

    const sizeStyles = {
        mini: { minWidth: 30, fontSize: 16, px: 2,  py: 0.75, minHeight: 34 },
        small: { minWidth: 120, fontSize: 16, px: 2,  py: 0.75, minHeight: 34 },
        mediumsmall:{ minWidth: 140, fontSize: 16, px: 3,  py: 1.25, minHeight: 44 },
        medium:{ minWidth: 200, fontSize: 16, px: 3,  py: 1.25, minHeight: 44 },
    };

    const baseSx = {
        textTransform: 'none',
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: '22px',
        textAlign: 'center',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: 0,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // запрет роста по флексу
        flex: '0 0 auto',
        // ховеры только если заданы
        ...(hoverColor && { '&:hover': { backgroundColor: hoverColor } }),
        ...(activeColor && { '&:active': { backgroundColor: activeColor } }),
        // мобильные правки идут ПОСЛЕ базовых размеров
        '@media (max-width:600px)': {
            minWidth: size === 'small' ? 100 : 160,
            fontSize: size === 'small' ? 13 : 14,
            px: size === 'small' ? 2 : 2.5,
            minHeight: size === 'small' ? 36 : 44,
        },
        ...sizeStyles[size],
    };

    return (
        <Button
            variant={variant}
            color="inherit"
            disableRipple
            startIcon={
                iconSrc ? (
                <Box component="img" src={iconSrc} alt="" sx={{ width: 22, height: 22, display: 'block' }} />
                ) : null
            }
            component={resolvedComponent}
            to={to}
            href={href}
            // правильно мержим sx: внутренний + внешний
            sx={{
                position: "relative",
                overflow: "visible",
                ...baseSx,
                ...(sxProp as any),
            }}
            {...props}
            >
            {children ?? text}
        </Button>
    );
}


export default function ConfirmEmailRequiredPage() {

    const {
        userEmail: email,
        userId,
        isSocialAccount,
        socialProviders,
    } = useContext(AuthContext);

    const reload = () => window.location.reload();
    const hasEmail = Boolean(email);
    const settingsPath = userId ? `/userlk/${userId}/settings` : "/userlogin";
    const isVkAccount = isSocialAccount && socialProviders.includes("vk");
    const accountSourceText = isVkAccount ? "аккаунта VK" : "аккаунта";

    const [isSending, setIsSending] = useState(false);
    const [info, setInfo] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);


    const resend = async () => {
        if (!email) {
            setError("Сначала укажите актуальный email в настройках аккаунта.");
            setInfo(null);
        return;
        }

        setIsSending(true);
        setError(null);
        setInfo(null);

        try {
        const data = await apiFetch<ResendConfirmEmailResponse>(
            `/user/api/resend_confirm_email/`,
            {
                baseUrl: BASE_URL,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
                redirectOnUnauthorized: false,
            }
        );

        setInfo(data?.message || "Письмо отправлено. Проверьте входящие и «Спам».");
        } catch (e) {
        if (e instanceof HttpError) {
            const msg =
                (e.data && (e.data.error || e.data.detail)) ||
                (e.status === 429 ? "Слишком часто. Подождите немного и попробуйте снова." : "Не удалось отправить письмо.");
            setError(msg);
            return;
        }
        setError("Сеть недоступна. Проверьте подключение и попробуйте снова.");
        } finally {
        setIsSending(false);
        }
    };



    return (
        <Box
            sx={{
                minHeight: "100dvh",
                display: "grid",
                placeItems: "center",
                p: { xs: 2, sm: 3 },
                backgroundColor: "var(--background-color-fon)",
            }}
        >
        <Box
            sx={{
                minWidth: 250,
                maxWidth: 600,                
                backgroundColor: "var(--background-color-fon)",
                borderRadius: "22px",
                boxShadow: "0 0 16px 4px rgba(0,0,0,0.12)",
                p: { xs: 2.5, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                textAlign: "center",
            }}
        >
            {/* Иллюстрация */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 560,
                    aspectRatio: "15 / 8",
                    borderRadius: "22px",
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src={imageUrl("/images/02_user/lk/conform_email.webp")}
                    alt="Email"
                    sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    }}
                    onError={(e) => {
                        e.currentTarget.style.display = "none";
                    }}
                />
            </Box>
            <Box
                sx={{
                    minHeight: "12vh"
                }}
            >
                <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                    {hasEmail ? "Подтвердите почту" : "Укажите почту"}
                </Typography>

                <Typography sx={{ opacity: 0.85, maxWidth: 520 }}>
                {hasEmail ? (
                    <>
                    Мы отправили письмо со ссылкой для активации аккаунта. Проверьте ящик <b>{email}</b> и откройте письмо от сервиса TrackMe.
                    </>
                ) : (
                    <>Для {accountSourceText} нужно указать актуальный email и подтвердить его по ссылке из письма.</>
                )}
                </Typography>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    pt: 1,
                }}
            >
            {hasEmail ? (
                <TavelButton
                    variant="contained"
                    size="medium"
                    text="Я подтвердил"
                    bgColor="var(--green-color)"
                    hoverColor="var(--button-green-hover)"
                    activeColor="var(--button-green-active)"
                    textColor="var(--color-text-main)"
                    onClick={reload}
                />
            ) : (
                <TavelButton
                    variant="contained"
                    size="medium"
                    text="Указать email"
                    to={settingsPath}
                    bgColor="var(--green-color)"
                    hoverColor="var(--button-green-hover)"
                    activeColor="var(--button-green-active)"
                    textColor="var(--color-text-main)"
                />
            )}
            </Box>

            {hasEmail ? (
                <Typography sx={{ fontSize: 16, opacity: 0.65, pt: 1 }}>
                    Если письма нет — проверьте “Спам” или{" "}
                    <Link
                    component="button"
                    onClick={resend}
                    disabled={isSending}
                    underline="always"
                    sx={{
                        cursor: isSending ? "default" : "pointer",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "var(--braun-color)",
                        opacity: isSending ? 0.6 : 1,
                        "&:hover": { opacity: isSending ? 0.6 : 0.85 },
                    }}
                    >
                    отправьте письмо повторно!
                    </Link>
                    {isSending ? (
                    <CircularProgress size={12} sx={{ ml: 1, verticalAlign: "middle" }} />
                    ) : null}
                </Typography>
            ) : (
                <Typography sx={{ fontSize: 16, opacity: 0.65, pt: 1 }}>
                    После сохранения email мы отправим письмо для подтверждения основной почты.
                </Typography>
            )}

            {/* сообщения */}
            {info ? (
                <Typography sx={{ fontSize: 16, pt: 1, color: "var(--success-color, #2e7d32)" }}>
                {info}
                </Typography>
            ) : null}

            {error ? (
                <Typography sx={{ fontSize: 16, pt: 1, color: "var(--error-color, #d32f2f)" }}>
                {error}
                </Typography>
            ) : null}
        </Box>
        </Box>
    );
}
